import React, { useState, useEffect } from 'react';
import { EnvelopeClosedIcon, ClockIcon, PersonIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { apiCall } from '../../../utils';
import { useToast } from '../../ui';
import SimpleModal from '../../composite/SimpleModal';

const STATUS_COLORS = {
	open: 'bg-brand-100 text-brand-800 dark:bg-brand-500/15 dark:text-brand-300',
	in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300',
	waiting_for_user: 'bg-purple-100 text-purple-800 dark:bg-purple-500/15 dark:text-purple-300',
	resolved: 'bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300',
	closed: 'bg-muted text-foreground',
};

const PRIORITY_COLORS = {
	low: 'bg-muted text-foreground',
	medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/15 dark:text-yellow-300',
	high: 'bg-orange-100 text-orange-800 dark:bg-orange-500/15 dark:text-orange-300',
	critical: 'bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300',
};

const TYPE_LABELS = {
	bug_report: 'Bug Report',
	feature_request: 'Feature Request',
	account_issue: 'Account Issue',
	scan_problem: 'Scan Problem',
	billing: 'Billing',
	general: 'General',
};

export default function SupportManagement() {
	const toast = useToast();
	const [tickets, setTickets] = useState([]);
	const [stats, setStats] = useState(null);
	const [loading, setLoading] = useState(true);
	const [, setError] = useState('');
	const [selectedTicket, setSelectedTicket] = useState(null);
	const [resolveTarget, setResolveTarget] = useState(null);
	const [resolutionText, setResolutionText] = useState('');
	const [filter, setFilter] = useState({ status: 'all', priority: 'all', type: 'all' });

	useEffect(() => {
		fetchTickets();
		fetchStats();
		// fetchTickets/fetchStats are plain functions recreated every render;
		// listing them would refetch on every render. They read `filter`, which
		// is the real trigger.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filter]);

	const fetchTickets = async () => {
		try {
			setLoading(true);
			const params = new URLSearchParams();
			if (filter.status !== 'all') params.append('status', filter.status);
			if (filter.priority !== 'all') params.append('priority', filter.priority);
			if (filter.type !== 'all') params.append('type', filter.type);

			const response = await apiCall(`/api/support/admin/tickets?${params}`);
			if (response.success) {
				setTickets(response.data);
			} else {
				throw new Error(response.error || 'Failed to fetch tickets');
			}
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const fetchStats = async () => {
		try {
			const response = await apiCall('/api/support/admin/stats');
			if (response.success) {
				setStats(response.data);
			}
		} catch (err) {
			console.error('Failed to fetch stats:', err);
		}
	};

	const handleAssignTicket = async (ticketId) => {
		try {
			const response = await apiCall(`/api/support/admin/tickets/${ticketId}/assign`, {
				method: 'POST',
				body: JSON.stringify({}), // Assigns to current user
			});
			if (response.success) {
				fetchTickets();
			}
		} catch (err) {
			toast.error('Failed to assign ticket', { description: err.message });
		}
	};

	const handleResolveTicket = (ticketId) => {
		setResolveTarget(ticketId);
		setResolutionText('');
	};

	const submitResolution = async () => {
		if (!resolutionText.trim() || !resolveTarget) return;
		try {
			const response = await apiCall(`/api/support/admin/tickets/${resolveTarget}/resolve`, {
				method: 'POST',
				body: JSON.stringify({
					resolution: resolutionText.trim(),
					resolutionType: 'user_education',
				}),
			});
			if (response.success) {
				setResolveTarget(null);
				fetchTickets();
				setSelectedTicket(null);
			}
		} catch (err) {
			toast.error('Failed to resolve ticket', { description: err.message });
		}
	};

	if (loading && tickets.length === 0) {
		return (
			<div className="flex items-center justify-center py-12">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
			</div>
		);
	}

	return (
		<div className="space-y-6">
			{/* Statistics Cards */}
			{stats && (
				<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
					<div className="bg-card p-4 rounded-lg shadow-sm border border-border">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-muted-foreground">Open Tickets</p>
								<p className="text-2xl font-bold text-foreground">
									{stats.byStatus?.find(s => s._id === 'open')?.count || 0}
								</p>
							</div>
							<EnvelopeClosedIcon className="w-8 h-8 text-brand-600" />
						</div>
					</div>
					<div className="bg-card p-4 rounded-lg shadow-sm border border-border">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-muted-foreground">In Progress</p>
								<p className="text-2xl font-bold text-foreground">
									{stats.byStatus?.find(s => s._id === 'in_progress')?.count || 0}
								</p>
							</div>
							<ClockIcon className="w-8 h-8 text-yellow-600" />
						</div>
					</div>
					<div className="bg-card p-4 rounded-lg shadow-sm border border-border">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-muted-foreground">Critical Priority</p>
								<p className="text-2xl font-bold text-foreground">
									{stats.byPriority?.find(p => p._id === 'critical')?.count || 0}
								</p>
							</div>
							<ExclamationTriangleIcon className="w-8 h-8 text-red-600" />
						</div>
					</div>
					<div className="bg-card p-4 rounded-lg shadow-sm border border-border">
						<div className="flex items-center justify-between">
							<div>
								<p className="text-sm text-muted-foreground">Avg Response Time</p>
								<p className="text-2xl font-bold text-foreground">
									{Math.round(stats.avgFirstResponseTime || 0)}m
								</p>
							</div>
							<PersonIcon className="w-8 h-8 text-green-600" />
						</div>
					</div>
				</div>
			)}

			{/* Filters */}
			<div className="bg-card p-4 rounded-lg shadow-sm border border-border">
				<div className="flex gap-4 flex-wrap">
					<select
						value={filter.status}
						onChange={(e) => setFilter({ ...filter, status: e.target.value })}
						className="px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500"
					>
						<option value="all">All Status</option>
						<option value="open">Open</option>
						<option value="in_progress">In Progress</option>
						<option value="waiting_for_user">Waiting for User</option>
						<option value="resolved">Resolved</option>
						<option value="closed">Closed</option>
					</select>
					<select
						value={filter.priority}
						onChange={(e) => setFilter({ ...filter, priority: e.target.value })}
						className="px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500"
					>
						<option value="all">All Priorities</option>
						<option value="low">Low</option>
						<option value="medium">Medium</option>
						<option value="high">High</option>
						<option value="critical">Critical</option>
					</select>
					<select
						value={filter.type}
						onChange={(e) => setFilter({ ...filter, type: e.target.value })}
						className="px-3 py-2 border border-input rounded-md focus:ring-brand-500 focus:border-brand-500"
					>
						<option value="all">All Types</option>
						<option value="bug_report">Bug Report</option>
						<option value="feature_request">Feature Request</option>
						<option value="account_issue">Account Issue</option>
						<option value="scan_problem">Scan Problem</option>
						<option value="billing">Billing</option>
						<option value="general">General</option>
					</select>
				</div>
			</div>

			{/* Tickets Table */}
			<div className="bg-card rounded-lg shadow-sm border border-border overflow-hidden">
				<table className="min-w-full divide-y divide-border">
					<thead className="bg-muted">
						<tr>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Ticket
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								User
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Type
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Priority
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Status
							</th>
							<th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="bg-card divide-y divide-border">
						{tickets.map(ticket => (
							<tr key={ticket._id} className="hover:bg-muted">
								<td className="px-6 py-4 whitespace-nowrap">
									<div>
										<div className="text-sm font-medium text-foreground">{ticket.ticketId}</div>
										<div className="text-sm text-muted-foreground">{ticket.title}</div>
									</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<div className="text-sm text-foreground">{ticket.user?.name || ticket.userEmail}</div>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<span className="text-sm text-foreground">{TYPE_LABELS[ticket.type]}</span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<span className={`px-2 py-1 text-xs rounded-full ${PRIORITY_COLORS[ticket.priority]}`}>
										{ticket.priority}
									</span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap">
									<span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[ticket.status]}`}>
										{ticket.status.replace('_', ' ')}
									</span>
								</td>
								<td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
									<button
										onClick={() => setSelectedTicket(ticket)}
										className="text-brand-600 hover:text-brand-900 mr-3"
									>
										View
									</button>
									{ticket.status === 'open' && (
										<button
											onClick={() => handleAssignTicket(ticket._id)}
											className="text-green-600 hover:text-green-900 mr-3"
										>
											Assign to Me
										</button>
									)}
									{['in_progress', 'waiting_for_user'].includes(ticket.status) && (
										<button
											onClick={() => handleResolveTicket(ticket._id)}
											className="text-purple-600 hover:text-purple-900"
										>
											Resolve
										</button>
									)}
								</td>
							</tr>
						))}
					</tbody>
				</table>

				{tickets.length === 0 && (
					<div className="text-center py-12">
						<EnvelopeClosedIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
						<p className="text-muted-foreground">No tickets found</p>
					</div>
				)}
			</div>

			{/* Resolve dialog - replaces a blocking prompt() */}
			<SimpleModal
				isOpen={!!resolveTarget}
				onClose={() => setResolveTarget(null)}
				title="Resolve ticket"
			>
				<div className="space-y-4">
					<textarea
						className="w-full rounded-md border border-input px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
						rows={4}
						placeholder="What resolved this ticket? The customer sees this."
						value={resolutionText}
						onChange={(e) => setResolutionText(e.target.value)}
						autoFocus
					/>
					<div className="flex justify-end gap-2">
						<button
							type="button"
							onClick={() => setResolveTarget(null)}
							className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
						>
							Cancel
						</button>
						<button
							type="button"
							onClick={submitResolution}
							disabled={!resolutionText.trim()}
							className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
						>
							Mark resolved
						</button>
					</div>
				</div>
			</SimpleModal>

			{/* Ticket Detail Modal */}
			{selectedTicket && (
				<TicketDetailModal
					ticket={selectedTicket}
					onClose={() => setSelectedTicket(null)}
					onUpdate={() => {
						fetchTickets();
						setSelectedTicket(null);
					}}
				/>
			)}
		</div>
	);
}

// Admin Ticket Detail Modal
function TicketDetailModal({ ticket, onClose, onUpdate }) {
	const toast = useToast();
	const [response, setResponse] = useState('');
	const [loading, setLoading] = useState(false);

	const handleAddSupportResponse = async (e) => {
		e.preventDefault();
		if (!response.trim()) return;

		setLoading(true);
		try {
			const result = await apiCall(`/api/support/admin/tickets/${ticket._id}/support-response`, {
				method: 'POST',
				body: JSON.stringify({ message: response }),
			});

			if (result.success) {
				setResponse('');
				onUpdate();
			}
		} catch (err) {
			toast.error('Failed to add response', { description: err.message });
		} finally {
			setLoading(false);
		}
	};

	return (
		<SimpleModal isOpen onClose={onClose} title={ticket.title} size="2xl">
			<div>
				<div className="flex items-center gap-3 text-sm">
					<span className={`px-2 py-1 rounded-full ${STATUS_COLORS[ticket.status]}`}>
						{ticket.status.replace('_', ' ')}
					</span>
					<span className={`px-2 py-1 rounded-full ${PRIORITY_COLORS[ticket.priority]}`}>
						{ticket.priority}
					</span>
					<span className="text-muted-foreground">{ticket.ticketId}</span>
				</div>

				<div className="p-6 overflow-y-auto max-h-[60vh]">
					{/* User Info */}
					<div className="mb-6 bg-brand-50 p-4 rounded-lg dark:bg-brand-500/10">
						<h3 className="font-medium text-foreground mb-2">User Information</h3>
						<p className="text-sm"><strong>Name:</strong> {ticket.user?.name || 'N/A'}</p>
						<p className="text-sm"><strong>Email:</strong> {ticket.userEmail}</p>
						<p className="text-sm"><strong>Role:</strong> {ticket.userRole || 'user'}</p>
					</div>

					{/* Technical Context */}
					{ticket.context && (
						<div className="mb-6 bg-muted p-4 rounded-lg">
							<h3 className="font-medium text-foreground mb-2">Technical Context</h3>
							<p className="text-sm"><strong>Browser:</strong> {ticket.context.browser}</p>
							<p className="text-sm"><strong>Platform:</strong> {ticket.context.platform}</p>
							<p className="text-sm"><strong>URL:</strong> {ticket.context.currentUrl}</p>
							<p className="text-sm"><strong>Time:</strong> {new Date(ticket.context.timestamp).toLocaleString()}</p>
						</div>
					)}

					{/* Original Message */}
					<div className="mb-6">
						<h3 className="font-medium text-foreground mb-2">Description</h3>
						<p className="text-foreground whitespace-pre-wrap">{ticket.description}</p>
					</div>

					{/* Technical Details (for bug reports) */}
					{ticket.technicalDetails && ticket.type === 'bug_report' && (
						<div className="mb-6 bg-red-50 p-4 rounded-lg dark:bg-red-500/10">
							<h3 className="font-medium text-foreground mb-2">Bug Details</h3>
							{ticket.technicalDetails.errorMessage && (
								<p className="text-sm mb-2"><strong>Error:</strong> {ticket.technicalDetails.errorMessage}</p>
							)}
							{ticket.technicalDetails.actionPerformed && (
								<p className="text-sm"><strong>Action:</strong> {ticket.technicalDetails.actionPerformed}</p>
							)}
						</div>
					)}

					{/* Responses */}
					{ticket.responses && ticket.responses.length > 0 && (
						<div className="space-y-4 mb-6">
							<h3 className="font-medium text-foreground">Conversation</h3>
							{ticket.responses.map((resp, index) => (
								<div
									key={index}
									className={`p-4 rounded-lg ${resp.from === 'support'
										? 'bg-green-50 ml-8 dark:bg-green-500/10'
										: 'bg-muted mr-8'
										}`}
								>
									<div className="flex items-center justify-between mb-2">
										<span className="font-medium text-sm">
											{resp.from === 'support' ? 'Support Team' : (ticket.user?.name || 'User')}
										</span>
										<span className="text-xs text-muted-foreground">
											{new Date(resp.timestamp).toLocaleString()}
										</span>
									</div>
									<p className="text-foreground whitespace-pre-wrap">{resp.message}</p>
								</div>
							))}
						</div>
					)}

					{/* Add Response Form */}
					{['open', 'in_progress', 'waiting_for_user'].includes(ticket.status) && (
						<form onSubmit={handleAddSupportResponse} className="border-t pt-4">
							<h3 className="font-medium text-foreground mb-3">Add Support Response</h3>
							<textarea
								value={response}
								onChange={(e) => setResponse(e.target.value)}
								placeholder="Type your response as support..."
								className="w-full px-3 py-2 border border-input rounded-lg focus:ring-brand-500 focus:border-brand-500"
								rows={4}
								disabled={loading}
							/>
							<div className="flex justify-end mt-3 gap-2">
								<button
									type="button"
									onClick={onClose}
									className="px-4 py-2 text-foreground bg-accent rounded-lg hover:bg-accent/80"
								>
									Close
								</button>
								<button
									type="submit"
									disabled={loading || !response.trim()}
									className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{loading ? 'Sending...' : 'Send as Support'}
								</button>
							</div>
						</form>
					)}
				</div>
			</div>
		</SimpleModal>
	);
}
